import { forwardRef } from "react";
import { cn } from "../../lib/cn";

/**
 * Phase 2 primitives — `<Input>`, `<Select>`, `<Textarea>` replace the
 * `.finput` / `.fctrl` / `.fsel` / `.fsel-ctrl` / `.ctextarea` families.
 * They forward refs so React Hook Form and focus() calls still work,
 * and honour aria-invalid via the `error` prop (red border + ring).
 */

const base = [
  "w-full font-sans text-sm text-rc-text-primary",
  "bg-rc-surface border rounded-lg",
  "px-3.5 py-2.5 outline-none",
  "transition-colors transition-shadow",
  "placeholder:text-[var(--rc-gray)]",
  "disabled:opacity-40 disabled:cursor-not-allowed",
].join(" ");

const borderBase = "border-rc-border";
const borderFocus = "focus:border-rc-accent-strong focus:shadow-[0_0_0_3px_rgba(64,117,193,0.12)]";
const borderError = "border-rc-red focus:border-rc-red focus:shadow-[0_0_0_3px_rgba(240,68,68,0.2)]";

export const Input = forwardRef(function Input({ className, error, ...props }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={error || undefined}
      className={cn(base, error ? borderError : cn(borderBase, borderFocus), className)}
      {...props}
    />
  );
});

// Inline SVG chevron, embedded as a data URL in the background. Matches
// the .fsel-ctrl background-image from common-ui.css so the dropdown
// affordance is consistent with legacy forms while we migrate.
const SELECT_CHEVRON =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%236b6b6b' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")";

export const Select = forwardRef(function Select({ className, error, children, ...props }, ref) {
  return (
    <select
      ref={ref}
      aria-invalid={error || undefined}
      className={cn(
        base,
        "appearance-none cursor-pointer pr-8 bg-no-repeat",
        error ? borderError : cn(borderBase, borderFocus),
        className,
      )}
      style={{ backgroundImage: SELECT_CHEVRON, backgroundPosition: "calc(100% - 12px) center" }}
      {...props}
    >
      {children}
    </select>
  );
});

export const Textarea = forwardRef(function Textarea({ className, error, ...props }, ref) {
  return (
    <textarea
      ref={ref}
      aria-invalid={error || undefined}
      className={cn(
        base,
        "resize-y leading-[1.6] min-h-[80px]",
        error ? borderError : cn(borderBase, borderFocus),
        className,
      )}
      {...props}
    />
  );
});

export function FieldLabel({ children, required, className }) {
  return (
    <label className={cn("text-[11.5px] font-semibold text-rc-text-secondary", className)}>
      {children}
      {required && <span className="ml-0.5 text-rc-red">*</span>}
    </label>
  );
}

export function FieldGroup({ children, className }) {
  return <div className={cn("flex flex-col gap-1.5", className)}>{children}</div>;
}

export function FieldError({ children }) {
  if (!children) return null;
  return <div className="mt-0.5 text-xs text-rc-red">{children}</div>;
}

export function FieldHint({ children }) {
  if (!children) return null;
  return <div className="mt-0.5 text-xs text-rc-text-dim">{children}</div>;
}

export default Input;
